import React from "react";
import { Mail, Linkedin } from "lucide-react";
import Reveal from "../Reveal";
import WhatsAppIcon from "../icons/WhatsAppIcon";

const FOUNDERS = [
  {
    initials: "KG",
    image: "/team/kshitij.jpg",
    name: "Kshitij Gupta",
    creds: "IIT Delhi · L.E.K. Consulting",
    bio: "Five years across manufacturing and supply chain. Anchors sourcing, production, quality control, and on-ground execution at Aurevia.",
    email: "kshitij.gupta@aureviastones.com",
    phone: "+91 87965 64619",
    waNumber: "918796564619",
    linkedin: "https://www.linkedin.com/in/kshitij-gupta-226797156/",
    linkedinHandle: "kshitij-gupta",
  },
  {
    initials: "RC",
    image: "/team/ritika.jpg",
    name: "Ritika Chaplot",
    creds: "IIT Delhi · BCG · Shyam Marbles",
    bio: "Backed by a 30-year family legacy in marble. Combines deep generational knowledge of stone with modern design sensibility.",
    email: "ritika.chaplot@aureviastones.com",
    phone: "+91 91165 66203",
    waNumber: "919116566203",
    linkedin: "https://www.linkedin.com/in/ritika-chaplot-825755159/",
    linkedinHandle: "ritika-chaplot",
  },
];

const FounderCard = ({ founder, index }) => {
  const slug = founder.name.toLowerCase().replace(/\s+/g, "-");
  return (
    <Reveal
      delay={index * 0.1}
      data-testid={`founder-card-${slug}`}
      className="group relative bg-brand-charcoal/40 border border-brand-gold/15 hover:border-brand-gold/35 transition-colors duration-500 p-8 md:p-10 lg:p-12 flex flex-col"
    >
      {/* gold corner accents */}
      <span
        aria-hidden
        className="absolute top-0 left-0 h-px w-0 bg-brand-gold/70 transition-all duration-700 group-hover:w-16"
      />
      <span
        aria-hidden
        className="absolute top-0 left-0 w-px h-0 bg-brand-gold/70 transition-all duration-700 group-hover:h-16"
      />
      <span
        aria-hidden
        className="absolute bottom-0 right-0 h-px w-0 bg-brand-gold/70 transition-all duration-700 group-hover:w-16"
      />
      <span
        aria-hidden
        className="absolute bottom-0 right-0 w-px h-0 bg-brand-gold/70 transition-all duration-700 group-hover:h-16"
      />

      {/* Portrait */}
      <div className="flex items-center gap-6 mb-8">
        <div
          className="relative h-20 w-20 md:h-24 md:w-24 rounded-full overflow-hidden ring-1 ring-brand-gold/30 shrink-0 bg-brand-charcoal"
        >
          <span
            aria-hidden
            className="absolute -inset-0.5 rounded-full bg-brand-gold/[0.08] blur-md"
          />
          {founder.image ? (
            <img
              src={founder.image}
              alt={founder.name}
              loading="lazy"
              className="relative h-full w-full object-cover"
            />
          ) : (
            <span className="relative flex h-full w-full items-center justify-center font-serif text-2xl md:text-3xl font-light tracking-[0.06em] text-brand-gold">
              {founder.initials}
            </span>
          )}
        </div>
        <div>
          <h3
            data-testid={`founder-name-${slug}`}
            className="font-serif text-2xl md:text-[28px] font-light text-brand-cream leading-tight"
          >
            {founder.name}
          </h3>
          <p className="mt-1.5 font-sans text-[10px] uppercase tracking-[0.28em] text-brand-gold/90">
            {founder.creds}
          </p>
        </div>
      </div>

      {/* Bio */}
      <p className="font-sans text-sm md:text-[15px] leading-relaxed text-brand-cream/65 mb-8 max-w-md">
        {founder.bio}
      </p>

      {/* Contact lines */}
      <div className="mt-auto space-y-3 pt-6 border-t border-white/10">
        <a
          href={`mailto:${founder.email}`}
          data-testid={`founder-email-${slug}`}
          className="group/contact flex items-center gap-3 font-sans text-sm text-brand-cream/75 hover:text-brand-gold transition-colors"
        >
          <span className="h-8 w-8 rounded-full border border-brand-gold/25 flex items-center justify-center text-brand-gold group-hover/contact:border-brand-gold/60 group-hover/contact:bg-brand-gold/[0.06] transition-colors">
            <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
          </span>
          <span className="truncate">{founder.email}</span>
        </a>
        <a
          href={`https://wa.me/${founder.waNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`founder-whatsapp-${slug}`}
          className="group/contact flex items-center gap-3 font-sans text-sm text-brand-cream/75 hover:text-brand-gold transition-colors"
        >
          <span className="h-8 w-8 rounded-full border border-brand-gold/25 flex items-center justify-center text-brand-gold group-hover/contact:border-brand-gold/60 group-hover/contact:bg-brand-gold/[0.06] transition-colors">
            <WhatsAppIcon className="h-3.5 w-3.5" strokeWidth={1.6} />
          </span>
          <span>{founder.phone}</span>
        </a>
        {founder.linkedin && (
          <a
            href={founder.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`founder-linkedin-${slug}`}
            className="group/contact flex items-center gap-3 font-sans text-sm text-brand-cream/75 hover:text-brand-gold transition-colors"
          >
            <span className="h-8 w-8 rounded-full border border-brand-gold/25 flex items-center justify-center text-brand-gold group-hover/contact:border-brand-gold/60 group-hover/contact:bg-brand-gold/[0.06] transition-colors">
              <Linkedin className="h-3.5 w-3.5" strokeWidth={1.5} />
            </span>
            <span>LinkedIn</span>
          </a>
        )}
      </div>
    </Reveal>
  );
};

export const ContactUs = () => {
  return (
    <section
      id="contact"
      data-testid="section-contact-us"
      className="relative py-16 md:py-24 border-t border-white/5"
    >
      <div className="editorial-container">
        <Reveal className="max-w-3xl mb-10 md:mb-14">
          <div className="flex items-center gap-4 mb-7">
            <span className="h-px w-10 bg-brand-gold/70" />
            <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold">
              Meet the Team
            </p>
          </div>
          <h2
            data-testid="contact-heading"
            className="font-serif font-light text-balance text-brand-cream leading-[1.02] tracking-tight"
            style={{ fontSize: "clamp(2rem, 4.4vw, 4rem)" }}
          >
            Meet the <span className="italic text-brand-gold/90">Founders.</span>
          </h2>
          <p className="mt-6 font-sans text-base md:text-lg text-brand-cream/65 max-w-xl">
            Two founders. One promise - marble done right, every time.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {FOUNDERS.map((f, i) => (
            <FounderCard key={f.name} founder={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
